import React from "react";
import { useState, useEffect } from "react";
import LotteryNumlotIndexSummaryContainer from "../components/lottery/containers/LotteryNumlotIndexSummaryContainer";
import LotteryNumlotIndexSummaryTemplate from "../components/lottery/templates/LotteryNumlotIndexSummaryTemplate";
import PageMeta from "../components/common/PageMeta";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { NEWS } from "../data/news";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { LOTTERY_TYPES } from "../config/lotteryConfig";
import { formatAmount } from "../utils/common/format";
import LotteryLocalNav from "../components/lottery/LotteryLocalNav";

const LOTTERY_KEYS = ["lt6", "lt7", "mlt", "nm4", "nm3", "bg5", "qcc"];

const LOTTERY_META = {
  lt6:  { path: "/loto6",    fontSize: "text-base" },
  lt7:  { path: "/loto7",    fontSize: "text-base" },
  mlt:  { path: "/miniloto", fontSize: "text-base" },
  nm4:  { path: "/numbers4", fontSize: "text-xl" },
  nm3:  { path: "/numbers3", fontSize: "text-xl" },
  bg5:  { path: "/bingo5",   fontSize: "text-xl" },
  qcc:  { path: "/qoochan",  fontSize: "text-xl" },
};

function Top() {
  const description = "ロト6・ロト7・ミニロト・ナンバーズ3・ナンバーズ4・ビンゴ5の抽選結果・当選番号・払戻金を公開し、「どんな出目のとき配当金が高くなるか」など過去データの統計的な分析・研究を行う無料の情報サイトです。当選予想・必勝法・攻略ツールではなく、宝くじの販売・購入代行や個人情報の入力もありません。";

  const [sortedKeys, setSortedKeys] = useState(LOTTERY_KEYS);

  useEffect(() => {
    const fetchLatestDates = async () => {
      const results = await Promise.all(
        LOTTERY_KEYS.map(async (key) => {
          const { data } = await supabase
            .schema("chatorahack")
            .rpc("get_lottery_numlot_index_summary", {
              lottery_key: key,
              limit_count: 1,
              offset_count: 0,
              sort_column: "round",
              sort_order: "DESC",
            });
          return { key, draw_date: data?.[0]?.draw_date ?? "0000-00-00" };
        })
      );
      const sorted = results
        .sort((a, b) => (a.draw_date > b.draw_date ? -1 : 1))
        .map((r) => r.key);
      setSortedKeys(sorted);
    };
    fetchLatestDates();
  }, []);

  return (
    <>
      <PageMeta
        title="トップページ"
        description={description}
        path="/"
        breadcrumbs={[{ name: "TOP", path: "/" }]}
      />

      {sortedKeys.map((key) => {
        const config = LOTTERY_TYPES[key];
        const meta = LOTTERY_META[key];
        const isQcc = key === "qcc";

        return (
          <React.Fragment key={key}>
            <LotteryLocalNav placement="mainHeader" lotteryKey={key} activeKey={key} />
            <section key={key} className="container mx-auto w-auto mb-32" id={key}>
              <div className={`flex justify-between items-center ${isQcc ? "mb-2" : "mb-2"} border-b border-b-2 border-primary-500 pb-2 gap-2`}>
                <h2 className="text-2xl font-bold text-primary-500">{config.label}</h2>
                <Link to={meta.path} className="text-sm font-bold text-primary-500 underline hover:no-underline ml-auto">
                  {config.label} 一覧
                </Link>
                <Link
                  to={config.statsPath}
                  className="text-sm font-bold text-primary-500 underline hover:no-underline"
                >
                  統計データ
                </Link>
              </div>
              {isQcc && (
                <p className="mb-2 text-[10px] text-primary-500/50 text-right">
                  ※着せかえクーちゃんは2026年4月13日（月）をもって販売を終了いたしました。
                </p>
              )}
              {/* 見出し下に追加 */}
              <div className="mb-8">
                <p className="mb-1 flex flex-wrap gap-2 text-base font-bold text-primary-500">
                  <span>{config.catchcopy}</span>
                </p>
                <p className="mb-1 flex flex-wrap gap-2 text-base font-bold text-primary-500">
                  <span>
                    最高配当{formatAmount(config.maxPrize)}
                    {config.maxPrizeCo && (
                      <>／キャリーオーバー時最高配当{formatAmount(config.maxPrizeCo)}</>
                    )}
                  （{config.maxPrizeLabel}）
                  </span>
                </p>
                <p className="flex flex-wrap gap-2 text-xs text-gray-800">
                  <span>抽選日：{config.drawDay}</span> / 
                  <span>購入単価：{formatAmount(config.price)}</span>
                </p>
              </div>
              <LotteryNumlotIndexSummaryContainer
                lotteryKey={key}
                title={config.label}
                lotteryPath={meta.path.replace("/", "")}
                Template={LotteryNumlotIndexSummaryTemplate}
                itemsPerPage={5}
                showMore={false}
                fontSize={meta.fontSize}
              />
              <div className="mt-4 mx-auto max-w-sm">
                <div className="mt-4 text-center">
                  <Link
                    to={`${meta.path}/stats`}
                    className="text-xs text-primary-500 underline hover:no-underline"
                  >
                    統計データを見る
                  </Link>
                </div>
                <div className="mt-4 mx-auto max-w-sm">
                  <Link
                    to={meta.path}
                    className="block py-2 text-primary-500 border border-primary-500 rounded-xl font-bold bg-base-50 hover:bg-primary-500 hover:text-base-50 transition-colors text-center"
                  >
                    MORE
                  </Link>
                </div>
              </div>
            </section>
          </React.Fragment>
        );
      })}

      <section className="container mx-auto w-auto mb-16" id="news">
        <div className="flex justify-between items-center mb-6 border-b border-b-2 border-primary-500 pb-2">
          <h2 className="text-2xl font-bold text-primary-500">お知らせ</h2>
          <Link to="/news" className="text-sm font-bold text-primary-500 underline hover:no-underline">お知らせ 一覧</Link>
        </div>

        <Accordion type="multiple" className="grid gap-4">
          {NEWS.slice().reverse().map((item, index) => (
            item.detail ? (
              <AccordionItem
                key={index}
                value={String(index)}
                className="border border-primary-500 rounded-lg bg-base-50"
              >
                <AccordionTrigger className="flex items-center px-4 py-4 gap-4 hover:no-underline [&>svg]:text-primary-500">
                  <span className="text-xs text-primary-500 shrink-0">{item.date}</span>
                  <span className="text-sm font-bold text-primary-500 text-left mr-auto">{item.title}</span>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div
                    className="text-sm text-primary-500 [&_a]:underline [&_a:hover]:opacity-70"
                    dangerouslySetInnerHTML={{ __html: item.detail }}
                  />
                </AccordionContent>
              </AccordionItem>
            ) : (
              <div key={index} className="border border-primary-500 rounded-lg bg-base-50 px-4 py-4 flex items-center gap-4">
                <span className="text-xs text-primary-500 shrink-0">{item.date}</span>
                <span className="text-sm font-bold text-primary-500">{item.title}</span>
              </div>
            )
          ))}
        </Accordion>
      </section>
    </>
  );
}

export default Top;
